import { useConfirmationDialog } from '@justomx/atlas-components'
import { useAtom, useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'

import { useRequiredParams } from 'hooks/params.hook'
import { useRegisterProduct } from 'hooks/registerProduct.hook'
import { ProductQuantitiesSchema } from 'types/assembly/model'
import { ZOD_ERROR } from 'utils/constants'
import { isValidLPNScan } from 'utils/format-helpers'
import { toastError } from 'utils/toast'
import {
  selectedProductAtom,
  productQuantitiesAtom,
  containerLocationAtom
} from '../Assemby.state'
import { useGoToScanLocation } from '../scan-location/ScanLocation.navigator'

export const useProductForm = () => {
  const navigate = useNavigate()
  const product = useAtomValue(selectedProductAtom)
  const containerLocation = useAtomValue(containerLocationAtom)
  const scanLocationPage = useGoToScanLocation()
  const { transferId, docNum } = useRequiredParams('transferId', 'docNum')
  const [total, setTotal] = useState(0)
  const [quantities, setQuantities] = useAtom(productQuantitiesAtom)
  const { dialogRef, open, value } = useConfirmationDialog()
  const rawProductQuantities = useLoaderData()
  const { register, loading } = useRegisterProduct()
  const [missing, setMissing] = useState(0)
  const [waste, setWaste] = useState(0)

  useEffect(() => {
    if (rawProductQuantities === null) {
      return
    }

    const result = ProductQuantitiesSchema.safeParse(rawProductQuantities)
    if (!result.success) {
      throw Error(ZOD_ERROR)
    }
    setQuantities(result.data.quantity || [])
    setMissing(result.data.missingQuantity || 0)
    setWaste(result.data.wasteQuantity || 0)
  }, [rawProductQuantities, setQuantities])

  useEffect(() => {
    const sum = quantities.reduce((acc, curr) => acc + curr, 0)
    setTotal(sum)
  }, [quantities])

  const onGoBack = () => {
    navigate(-1)
  }

  const onAddQuantity = (quantity: string) => {
    if (!quantity.length) {
      return
    }
    if (+quantity <= 0) {
      toastError('Ingrese una cantidad válida')
      return
    }

    const newQuantities = [...quantities, +quantity]
    setQuantities(newQuantities)
  }

  const onDeleteQuantity = (index: number) => {
    setQuantities((value) => value.filter((_, idx) => idx !== index))
  }

  const onConfirmQuantity = (scanned?: string) => {
    const container = scanned || (typeof value === 'string' ? value : undefined)
    if (!container) {
      return
    }

    if (containerLocation.container !== container) {
      scanLocationPage.go(transferId, docNum, container)
      return
    }
    register(container, containerLocation.location)
  }

  const handleScanPallet = (scanned: string) => {
    if (!product) {
      return
    }
    const globalTotal = missing + waste + total

    if (!isValidLPNScan(scanned)) {
      toastError('El código de la tarima escaneado no es válido.')
      return
    }
    if (total === 0) {
      toastError('Asegurate de registrar la cantidad antes de continuar.')
      return
    }
    if (globalTotal < product.quantity) {
      open(scanned)
    } else if (globalTotal > product.quantity) {
      toastError(
        'La cantidad no fue registrada, porque ingresaste una cantidad mayor a la solicitada. Intenta de nuevo.'
      )
    } else {
      onConfirmQuantity(scanned)
    }
  }

  return {
    product,
    total,
    dialogRef,
    quantities,
    loading,
    actions: {
      onGoBack,
      handleScanPallet,
      setTotal,
      onConfirmQuantity,
      onAddQuantity,
      onDeleteQuantity
    }
  }
}
